import { useState } from "react";
import { toast } from "sonner";
import { Mail, X, Video, MessageCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { contactMessage } from "@/lib/api";

const TOPICS = ["Question", "My story", "Thank-you", "Comment"];

/* Self-contained Contact Us button + dialog. Drop anywhere: <ContactButton dark /> or <ContactButton dark iconOnly /> */
export const ContactButton = ({ dark = false, iconOnly = false, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState("Question");
  const [consultation, setConsultation] = useState(false);
  const [channel, setChannel] = useState("zoom");
  const [form, setForm] = useState({ name: "", email: "", message: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await contactMessage({
        name: form.name.trim(),
        email: form.email.trim(),
        topic,
        message: form.message.trim(),
        consultation,
        consultation_channel: consultation ? channel : null,
        consultation_note: consultation ? form.note.trim() : null,
      });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setOpen(false);
    if (sent) {
      setSent(false);
      setForm({ name: "", email: "", message: "", note: "" });
      setConsultation(false);
      setTopic("Question");
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="contact-open-btn"
        aria-label="Contact us"
        className={`inline-flex items-center gap-2 border-2 ${iconOnly ? "px-2.5" : "px-4"} py-2 font-bold uppercase tracking-widest text-sm transition-colors ${
          dark
            ? "border-white bg-transparent text-white hover:bg-[#E60000] hover:border-[#E60000]"
            : "border-black bg-white text-black hover:bg-black hover:text-white"
        } ${className}`}
      >
        <Mail className="w-4 h-4" /> {!iconOnly && "Contact Us"}
      </button>

      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent
          className="rounded-none border-2 border-black bg-white p-0 max-w-lg shadow-brutal max-h-[92vh] overflow-y-auto"
          data-testid="contact-dialog"
          hideClose
        >
          <button
            onClick={close}
            className="absolute right-3 top-3 z-10 border-2 border-black w-9 h-9 flex items-center justify-center bg-white hover:bg-[#E60000] hover:text-white transition-colors"
            aria-label="Close"
            data-testid="contact-dialog-close"
          >
            <X className="w-4 h-4" />
          </button>
          <DialogHeader className="p-6 pr-16 border-b-2 border-black">
            <DialogTitle className="font-display text-3xl uppercase leading-none">Contact us now</DialogTitle>
            <DialogDescription className="text-neutral-700 mt-2">
              Questions, your story, a thank-you note, or thoughts on the books
              or the site — a real human reads every message personally.
            </DialogDescription>
          </DialogHeader>

          {sent ? (
            <div className="p-6" data-testid="contact-success">
              <div className="border-2 border-black bg-[#F5F2EC] p-5">
                <div className="font-display text-2xl uppercase">Message received.</div>
                <p className="mt-2 text-sm font-medium text-neutral-800 leading-relaxed">
                  Thank you for reaching out{form.name ? `, ${form.name}` : ""}. We read
                  everything personally and reply as soon as we can{consultation ? " — we'll write back to set up your consultation" : ""}.
                  Keep going. One page, one day at a time.
                </p>
              </div>
              <button
                onClick={close}
                data-testid="contact-success-close"
                className="mt-6 w-full h-12 bg-black text-white border-2 border-black font-bold uppercase tracking-widest text-sm hover:bg-[#E60000] transition-colors"
              >
                Back to the site
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="contact-topics">
                {TOPICS.map((t) => (
                  <button
                    type="button"
                    key={t}
                    onClick={() => setTopic(t)}
                    data-testid={`contact-topic-${t.toLowerCase().replace(/[^a-z]/g, "")}`}
                    className={`py-2.5 px-2 border-2 border-black font-bold uppercase tracking-widest text-[11px] transition-colors ${
                      topic === t ? "bg-black text-white" : "bg-white text-black hover:bg-neutral-100"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cname" className="text-xs uppercase tracking-[0.25em] font-bold">Your name</Label>
                  <Input
                    id="cname"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="contact-name-input"
                    className="rounded-none border-2 border-black h-11 mt-2 focus-visible:ring-0 focus-visible:border-[#E60000]"
                  />
                </div>
                <div>
                  <Label htmlFor="cemail" className="text-xs uppercase tracking-[0.25em] font-bold">Your email</Label>
                  <Input
                    id="cemail"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="contact-email-input"
                    className="rounded-none border-2 border-black h-11 mt-2 focus-visible:ring-0 focus-visible:border-[#E60000]"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="cmsg" className="text-xs uppercase tracking-[0.25em] font-bold">Your message</Label>
                <Textarea
                  id="cmsg"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message-input"
                  placeholder="Write as much or as little as you need. No judgment here — ever."
                  className="rounded-none border-2 border-black mt-2 min-h-[110px] focus-visible:ring-0 focus-visible:border-[#E60000]"
                />
              </div>

              <div className="border-2 border-black" data-testid="contact-consultation-box">
                <button
                  type="button"
                  onClick={() => setConsultation(!consultation)}
                  data-testid="contact-consultation-toggle"
                  aria-pressed={consultation}
                  className={`w-full flex items-center gap-3 p-4 text-left transition-colors ${consultation ? "bg-black text-white" : "bg-white hover:bg-neutral-100"}`}
                >
                  <span className={`w-5 h-5 border-2 flex-shrink-0 flex items-center justify-center ${consultation ? "border-white bg-[#E60000]" : "border-black"}`}>
                    {consultation && <span className="w-2.5 h-2.5 bg-white" />}
                  </span>
                  <span>
                    <span className="block font-bold uppercase tracking-widest text-xs">I'd like a free one-on-one consultation</span>
                    <span className={`block text-xs mt-0.5 ${consultation ? "text-neutral-300" : "text-neutral-600"}`}>
                      A private video call about your situation — no cost, no judgment.
                    </span>
                  </span>
                </button>
                {consultation && (
                  <div className="border-t-2 border-black p-4 bg-[#F5F2EC]" data-testid="contact-consultation-options">
                    <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#E60000] mb-3">How should we meet?</div>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setChannel("zoom")}
                        data-testid="contact-channel-zoom"
                        className={`py-3 border-2 border-black font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-2 transition-colors ${channel === "zoom" ? "bg-[#E60000] text-white" : "bg-white hover:bg-neutral-100"}`}
                      >
                        <Video className="w-4 h-4" /> Zoom
                      </button>
                      <button
                        type="button"
                        onClick={() => setChannel("whatsapp")}
                        data-testid="contact-channel-whatsapp"
                        className={`py-3 border-2 border-black font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-2 transition-colors ${channel === "whatsapp" ? "bg-[#E60000] text-white" : "bg-white hover:bg-neutral-100"}`}
                      >
                        <MessageCircle className="w-4 h-4" /> WhatsApp
                      </button>
                    </div>
                    <Label htmlFor="cnote" className="text-xs uppercase tracking-[0.25em] font-bold mt-4 inline-block">Your time zone / best times (optional)</Label>
                    <Input
                      id="cnote"
                      value={form.note}
                      onChange={(e) => setForm({ ...form, note: e.target.value })}
                      data-testid="contact-note-input"
                      placeholder="e.g. Evenings, US Eastern"
                      className="rounded-none border-2 border-black h-11 mt-2 bg-white focus-visible:ring-0 focus-visible:border-[#E60000]"
                    />
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="w-full h-12 bg-[#E60000] text-white border-2 border-black font-bold uppercase tracking-widest text-sm hover:bg-black transition-colors disabled:opacity-60"
              >
                {submitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
